import type * as React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Textarea from '@cloudscape-design/components/textarea';
import { toast } from '@/lib/notifications';
import type { TenantId } from '@etiquetachef/types';
import { requestTenantDeletionCallable } from '@/lib/callables';
import { tenantDeletionErrorMessage } from './tenantDeletionError';

type Props = {
  open: boolean;
  tenantId: TenantId;
  tenantName: string;
  onClose: () => void;
};

/**
 * Dialog de encerramento (soft-delete) de tenant com dupla confirmação.
 *
 * Origem: sem issue — commit 3d9a9f21c, 2026-05-05 10:05 +0200 (Sprint 19 PR
 * 3d: UI para encerrar/restaurar tenant).
 *
 * Por quê: encerrar derruba o acesso de todos os usuários do tenant e para a
 * cobrança — a dupla confirmação (nome exato + motivo) evita clicar no tenant
 * errado de uma lista parecida.
 *  1. Digitar o nome do tenant (case-sensitive, match exato)
 *  2. Preencher motivo (vai para o audit; o limite de tamanho é do schema do
 *     callable)
 *
 * Contrato: chama `requestTenantDeletion` com `{ tenantId, reason }`. O
 * callable cancela a assinatura no gateway, voida invoices em aberto, bumpa
 * `sessionEpoch` (revoga sessões), audita e dispara o e-mail
 * `LIFECYCLE_TENANT_DELETED`. A retenção LGPD (apaga subcoleções) roda 90 dias
 * depois; até lá `RestoreTenantDialog` desfaz. Erro vira `Alert` dentro do
 * modal (#1021 — o toast ficava atrás do overlay); cobrança viva
 * no gateway que não pôde ser parada vira instrução (`tenantDeletionError`).
 *
 * Usado por: `features/billing/BillingActions.tsx` (aba Cobrança do
 * `TenantDetailPage`).
 *
 * Arquitetura: toda a lógica é do callable
 * (`apps/functions/src/admin/requestTenantDeletion.ts`); aqui só coleta a
 * confirmação.
 *
 * Falhas conhecidas:
 *  - 2026-10-01 (#789): excluir tenant deixava a assinatura do gateway
 *    cobrando; o servidor passou a recusar com `live_gateway_billing`, e o
 *    diálogo traduz o código em instrução.
 *  - 2026-10-25 (#1021): o erro do callable ia só por toast, atrás do overlay
 *    do Modal — quem clicava em confirmar não via nada. Agora é `Alert` no modal.
 *
 * Issues: #789, #1021 (erro dentro do modal; mesmo achado do CRM, #174).
 */
export function DeleteTenantDialog({
  open,
  tenantId,
  tenantName,
  onClose,
}: Props): React.JSX.Element {
  const { t } = useTranslation();
  const [confirmName, setConfirmName] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function close(): void {
    setConfirmName('');
    setReason('');
    setError(null);
    onClose();
  }

  const nameMatches = confirmName === tenantName;
  const reasonValid = reason.trim().length > 0;
  const canSubmit = nameMatches && reasonValid && !submitting;

  async function handleSubmit(): Promise<void> {
    if (!nameMatches) {
      setError(t('tenantDetail.actions.deleteDialog.nameMismatch'));
      return;
    }
    if (!reasonValid) {
      setError(t('tenantDetail.actions.reasonRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await requestTenantDeletionCallable({ tenantId, reason: reason.trim() });
      toast.success(t('tenantDetail.actions.deleteDialog.success'));
      close();
    } catch (err) {
      // #789: cobrança viva no gateway vira instrução, não o código cru.
      setError(tenantDeletionErrorMessage(err, t, 'close'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      visible={open}
      // #1021 (2ª auditoria 2026-10-25 15:52 +0100): Esc/X/clique fora fechavam o modal com a chamada
      // em voo, e a falha caía no `Alert` de um modal já fechado — ninguém a via (o Cancelar já
      // ficava desligado; o `onDismiss` não).
      onDismiss={(): void => {
        if (!submitting) close();
      }}
      header={t('tenantDetail.actions.deleteDialog.title')}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={close} disabled={submitting}>
              {t('tenantDetail.actions.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={(): void => {
                void handleSubmit();
              }}
              disabled={!canSubmit}
              loading={submitting}
            >
              {t('tenantDetail.actions.deleteDialog.confirm')}
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Alert type="warning">{t('tenantDetail.actions.deleteDialog.warning')}</Alert>

        <FormField
          label={t('tenantDetail.actions.deleteDialog.confirmNameLabel', { name: tenantName })}
        >
          <Input
            value={confirmName}
            onChange={({ detail }): void => {
              setConfirmName(detail.value);
            }}
            autoComplete={false}
            spellcheck={false}
            disabled={submitting}
            placeholder={tenantName}
          />
        </FormField>

        <FormField label={t('tenantDetail.actions.reasonLabel')}>
          <Textarea
            value={reason}
            onChange={({ detail }): void => {
              setReason(detail.value);
              // #1021 (2026-10-25 15:00 +0100): digitar o motivo tira o "motivo obrigatório" do Alert.
              setError(null);
            }}
            rows={3}
            disabled={submitting}
            placeholder={t('tenantDetail.actions.reasonPlaceholder')}
          />
        </FormField>
        {/* Auditoria 2026-10-25 14:29 +0100 (#1021): o erro ia só por toast, que fica atrás do overlay
            do Modal (mesmo achado do CRM, #174). Fica dentro do modal, junto do footer. */}
        {error !== null && <Alert type="error">{error}</Alert>}
      </SpaceBetween>
    </Modal>
  );
}
